import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        primary: "bg-[var(--color-steel)] text-white hover:bg-[var(--color-steel-dark)] shadow-sm",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        danger:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        warning:
          "bg-[var(--color-amber)] text-white hover:bg-[var(--color-amber-dark)] shadow-sm",
        outline:
          "border bg-background text-foreground hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        md: "h-9 px-4 py-2 has-[>svg]:px-3",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

interface ButtonProps extends React.ComponentProps<"button">,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  fullWidth?: boolean;
  hideTextOnMobile?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  icon: Icon,
  fullWidth,
  hideTextOnMobile,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size }),
        fullWidth && "w-full",
        className,
      )}
      {...props}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {hideTextOnMobile ? (
        <span className="hidden sm:inline">{children}</span>
      ) : (
        children
      )}
    </Comp>
  );
}

// IconButton: a button that renders just an icon
interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ComponentType<{ className?: string }>;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "primary";
  size?: "sm" | "default" | "lg";
}

function IconButton({ icon: Icon, variant = "ghost", size = "default", className, ...props }: IconButtonProps) {
  const variantMap: Record<string, string> = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    primary: "bg-[var(--color-steel)] text-white hover:bg-[var(--color-steel-dark)] shadow-md",
    destructive: "bg-destructive text-white hover:bg-destructive/90",
    outline: "border bg-background text-foreground hover:bg-accent hover:text-accent-foreground",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    link: "text-primary underline-offset-4 hover:underline",
  };

  const sizeMap: Record<string, string> = {
    sm: "w-8 h-8",
    default: "w-9 h-9",
    lg: "w-10 h-10",
  };

  return (
    <button
      className={cn(
        "inline-flex items-center justify-center rounded-md transition-all disabled:pointer-events-none disabled:opacity-50 outline-none",
        variantMap[variant] || variantMap.ghost,
        sizeMap[size] || sizeMap.default,
        className,
      )}
      {...props}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

export { Button, IconButton, buttonVariants };
